import { useReactiveVar } from "@apollo/client";
import { SearchOutlined } from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputRef,
  Select,
  Space,
  Table,
} from "antd";
import { ColumnsType } from "antd/es/table";
import type { ColumnType, FilterDropdownProps } from "antd/es/table/interface";
import dayjs, { Dayjs } from "dayjs";
import * as FileSaver from "file-saver";
import { Dispatch, SetStateAction, useState, useEffect, useRef } from "react";
import Highlighter from "react-highlight-words";
import XlsxPopulate from "xlsx-populate/browser/xlsx-populate";
import { EntryPackageQueryDataType } from "./types";
import Spinner from "../../../components/layout/spinner/Spinner";
import NoPermission from "../../../components/others/NoPermission";
import ContentTitle from "../../../components/ui/ContentTitle";
import MarketDropdown from "../../../components/ui/MarketDropdown";
import { useGetUserProgramsByServiceQuery } from "../../../graphql/operations/get-user-program-by-service";
import { useEntryPackageOrderListQuery } from "../../../graphql/operations/clubApp/EntryPackage/getEntryPackageOrderList";
import { __currentUser__ } from "../../../graphql/policies";
import { MarketType, ServiceTypes } from "../../../types/types";
import { renderDecodedId } from "../../../utils/renderDecodedId";
import "./EntryPackageQuery.scss";

interface EntryPackageQueryProps {
  setSelectedService: Dispatch<SetStateAction<ServiceTypes>>;
  setSelectSubItem: Dispatch<SetStateAction<string | undefined>>;
}
const EntryPackageQuery = ({
  setSelectedService,
  setSelectSubItem,
}: EntryPackageQueryProps) => {
  const { RangePicker } = DatePicker;
  const [form] = Form.useForm();
  const [dataDisplay, setDataDisplay] = useState<EntryPackageQueryDataType[]>(
    [],
  );
  const [filteredData, setFilteredData] = useState<EntryPackageQueryDataType[]>(
    [],
  );
  const [showDownload, setShowDownload] = useState(false);
  const [selectedMarket, setSelectedMarket] = useState<MarketType>("KR");
  const isHK = selectedMarket === "HK";
  const isTW = selectedMarket === "TW";
  const [showData, setShowData] = useState(false);
  const [orderStartDate, setOrderStartDate] = useState("");
  const [orderEndDate, setOrderEndDate] = useState("");
  const [orderId, setOrderId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const currentUser = useReactiveVar(__currentUser__);

  useEffect(() => {
    if (currentUser?.afUserMarkets && currentUser?.afUserMarkets[0]?.market) {
      if (currentUser.afUserMarkets.length === 1) {
        // Market dropdown is hidden for single-market accounts, so force the
        // query to use that account's only permitted market instead of the
        // "KR" default.
        setSelectedMarket(currentUser.afUserMarkets[0]?.market as MarketType);
      } else {
        setSelectedMarket("KR");
      }
    }
  }, [currentUser]);

  const { data: userPrograms, loading: userProgramsLoading } =
    useGetUserProgramsByServiceQuery({
      variables: {
        userId: parseInt(renderDecodedId(currentUser?.id)),
        serviceId: 2,
      },
    });
  const { data, refetch } = useEntryPackageOrderListQuery({
    variables: {
      market: selectedMarket,
      orderStartDate: orderStartDate,
      orderEndDate: orderEndDate,
      orderId: orderId,
      customerName: customerName,
      phoneNumber: phoneNumber,
    },
    skip: !showData,
  });

  const searchInput = useRef<InputRef>(null);
  const handleSearch = (
    selectedKeys: any,
    confirm: Function,
    dataIndex: string,
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: Function) => {
    clearFilters();
    setSearchText("");
  };

  const handleSubmit = () => {
    const { orderDate, orderId, customerName, phoneNumber } =
      form.getFieldsValue();
    const startDate = orderDate ? orderDate[0].format("YYYY-MM-DD") : undefined;
    const endDate = orderDate ? orderDate[1].format("YYYY-MM-DD") : undefined;
    setOrderStartDate(startDate);
    setOrderEndDate(endDate);
    setOrderId(orderId ?? "");
    setCustomerName(customerName ?? "");
    setPhoneNumber(phoneNumber ?? "");
    setShowData(true);
    refetch();
  };

  const handleMarketChange = (value: React.SetStateAction<MarketType>) => {
    form.resetFields();
    setOrderStartDate("");
    setOrderEndDate("");
    setOrderId("");
    setCustomerName("");
    setPhoneNumber("");
    setSearchText("");
    setSearchedColumn("");
    setDataDisplay([]);
    setFilteredData([]);
    setShowData(false);
    setShowDownload(false);
    setSelectedMarket(value);
  };

  const getColumnSearchProps = (dataIndex: string): ColumnType<any> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: FilterDropdownProps) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0] as string}
          onChange={(e) =>
            setSelectedKeys(
              e.target.value ? [e.target.value] : ([] as string[]),
            )
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText(selectedKeys[0] as string);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button type="link" size="small" onClick={close}>
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ?.toString()
        .toLowerCase()
        .includes(value.toString().toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const getColumnDateRangeProps =
    (): ColumnType<EntryPackageQueryDataType> => ({
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
        close,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <RangePicker
            value={
              selectedKeys[0]
                ? ([
                    dayjs((selectedKeys[0] as string).split("|")[0]),
                    dayjs((selectedKeys[0] as string).split("|")[1]),
                  ] as [Dayjs, Dayjs])
                : null
            }
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setSelectedKeys([
                  `${dates[0].format("YYYY-MM-DD")}|${dates[1].format("YYYY-MM-DD")}`,
                ]);
              } else {
                setSelectedKeys([]);
              }
            }}
            style={{ marginBottom: 8 }}
          />
          <Space style={{ display: "flex" }}>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => clearFilters && handleReset(clearFilters)}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
            <Button type="link" size="small" onClick={close}>
              close
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
      onFilter: (value, record) => {
        const [start, end] = (value as string).split("|");
        if (!record.createdAt) return false;
        const date = dayjs(record.createdAt).format("YYYY-MM-DD");
        return date >= start && date <= end;
      },
    });

  const handleDownload = async () => {
    if (!filteredData || filteredData.length === 0) {
      console.error("No data to export");
      return;
    }

    const password = `abc${dayjs().format("YYYYMMDD")}`;

    const formattedData = filteredData.map(
      ({
        packageNo,
        orderId,
        createdAt,
        customerName,
        phoneNumber,
        fullAddress,
        email,
        paymentMethod,
        isAdvertisingInfoAgreed,
        isMarketingInfoCollectionAgreed,
        memberPaymentInfo,
        memberChildren,
      }) => {
        const accountHolderName =
          memberPaymentInfo?.paymentType === 1
            ? (memberPaymentInfo?.accountHolderName ?? "")
            : "";
        const bankBirthDate =
          memberPaymentInfo?.paymentType === 1
            ? (memberPaymentInfo?.birthDate ?? "")
            : "";
        const bankName =
          memberPaymentInfo?.paymentType === 1
            ? (memberPaymentInfo?.bankName ?? "")
            : "";
        const bankAccountNumber =
          memberPaymentInfo?.paymentType === 1
            ? (memberPaymentInfo?.paymentNumber ?? "")
            : "";
        const creditCardCompany =
          memberPaymentInfo?.paymentType === 2
            ? isHK
              ? (memberPaymentInfo?.cardIssuer ?? "")
              : (memberPaymentInfo?.creditCardBankName ?? "")
            : "";
        const creditCardNumber =
          memberPaymentInfo?.paymentType === 2
            ? (memberPaymentInfo?.paymentNumber ?? "")
            : "";
        const cardHolderName =
          memberPaymentInfo?.paymentType === 2
            ? (memberPaymentInfo?.accountHolderName ?? "")
            : "";
        const creditCardExpirationDate =
          memberPaymentInfo?.paymentType === 2
            ? `${memberPaymentInfo?.expiryMonth ?? ""}/${memberPaymentInfo?.expiryYear ?? ""}`
            : "";

        if (isTW) {
          return {
            "Order No.": `${packageNo}-${orderId}`,
            "Order Date": dayjs(createdAt).format("YYYY-MM-DD"),
            "Contract Holder": customerName,
            "Contact Number": phoneNumber,
            "Shipping Address": fullAddress,
            "Email Address": email,
            "Optional Consent 1": isAdvertisingInfoAgreed ? "Y" : "N",
            "Optional Consent 2": isMarketingInfoCollectionAgreed ? "Y" : "N",
            "Child's Name1": memberChildren?.[0]?.childName || "",
            "Child's Gender1": memberChildren?.[0]?.childGender || "",
            "Child's Date of Birth1":
              memberChildren?.[0]?.childBirthDate?.split("T")[0] || "",
            "Child's Name2": memberChildren?.[1]?.childName || "",
            "Child's Gender2": memberChildren?.[1]?.childGender || "",
            "Child's Date of Birth2":
              memberChildren?.[1]?.childBirthDate?.split("T")[0] || "",
            "Child's Name3": memberChildren?.[2]?.childName || "",
            "Child's Gender3": memberChildren?.[2]?.childGender || "",
            "Child's Date of Birth3":
              memberChildren?.[2]?.childBirthDate?.split("T")[0] || "",
          };
        }

        if (isHK) {
          return {
            "Order No.": `${packageNo}-${orderId}`,
            "Order Date": dayjs(createdAt).format("YYYY-MM-DD"),
            "Contract Holder": customerName,
            "Contact Number": phoneNumber,
            "Shipping Address": fullAddress,
            "WFC Membership Fee Payment Method": paymentMethod,
            "Account Holder Name": accountHolderName,
            "Date of Birth": bankBirthDate,
            "Bank Name": bankName,
            "Bank Account Number": bankAccountNumber,
            "Credit Card Company": creditCardCompany,
            "Credit Card Number": creditCardNumber,
            "Card Holder Name": cardHolderName,
            "Credit Card Expiration Date": creditCardExpirationDate,
            "Optional Consent 1": isAdvertisingInfoAgreed ? "Y" : "N",
            "Optional Consent 2": isMarketingInfoCollectionAgreed ? "Y" : "N",
            "Child's Name1": memberChildren?.[0]?.childName || "",
            "Child's Gender1": memberChildren?.[0]?.childGender || "",
            "Child's Date of Birth1":
              memberChildren?.[0]?.childBirthDate?.split("T")[0] || "",
            "Child's Name2": memberChildren?.[1]?.childName || "",
            "Child's Gender2": memberChildren?.[1]?.childGender || "",
            "Child's Date of Birth2":
              memberChildren?.[1]?.childBirthDate?.split("T")[0] || "",
            "Child's Name3": memberChildren?.[2]?.childName || "",
            "Child's Gender3": memberChildren?.[2]?.childGender || "",
            "Child's Date of Birth3":
              memberChildren?.[2]?.childBirthDate?.split("T")[0] || "",
          };
        }

        return {
          주문번호: `${packageNo}-${orderId}`,
          주문일: dayjs(createdAt).format("YYYY-MM-DD"),
          계약자명: customerName,
          연락처: phoneNumber,
          주소: fullAddress,
          이메일: email,
          "클럽 회원비 결제등록 정보": paymentMethod,
          예금주명: accountHolderName,
          생년월일: bankBirthDate,
          은행명: bankName,
          계좌번호: bankAccountNumber,
          카드사: creditCardCompany,
          카드번호: creditCardNumber,
          카드소유자명: cardHolderName,
          카드유효기간: creditCardExpirationDate,
          자녀이름1: memberChildren?.[0]?.childName || "",
          성별1: memberChildren?.[0]?.childGender || "",
          생년월일1: memberChildren?.[0]?.childBirthDate?.split("T")[0] || "",
          자녀이름2: memberChildren?.[1]?.childName || "",
          성별2: memberChildren?.[1]?.childGender || "",
          생년월일2: memberChildren?.[1]?.childBirthDate?.split("T")[0] || "",
          자녀이름3: memberChildren?.[2]?.childName || "",
          성별3: memberChildren?.[2]?.childGender || "",
          생년월일3: memberChildren?.[2]?.childBirthDate?.split("T")[0] || "",
          "Optional Consent 1": isAdvertisingInfoAgreed ? "Yes" : "No",
          "Optional Consent 2": isMarketingInfoCollectionAgreed ? "Yes" : "No",
        };
      },
    );

    const workbook = await XlsxPopulate.fromBlankAsync();
    const sheet = workbook.sheet(0).name("Data");

    const headers = Object.keys(
      formattedData[0],
    ) as (keyof (typeof formattedData)[0])[];
    headers.forEach((header, colIdx) => {
      sheet.cell(1, colIdx + 1).value(header);
    });
    formattedData.forEach((row, rowIdx) => {
      headers.forEach((header, colIdx) => {
        sheet.cell(rowIdx + 2, colIdx + 1).value(row[header] ?? "");
      });
    });

    const buffer = await workbook.outputAsync({ password });
    const dataBlob = new Blob([buffer as ArrayBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    FileSaver.saveAs(
      dataBlob,
      `Entry-package-query-${dayjs().format("YYYY-MM-DD")}.xlsx`,
    );
  };

  useEffect(() => {
    setSelectedService(ServiceTypes.CLUB_APP);
    setSelectSubItem("Entry Package Query");
  }, []);

  useEffect(() => {
    if (data?.entryPackageOrderList) {
      const list =
        (data?.entryPackageOrderList as EntryPackageQueryDataType[]) || [];
      setDataDisplay(list);
      setFilteredData(list);
    }
  }, [data]);

  const columnTitles: Record<string, { kr: string; en: string }> = {
    orderId: { kr: "주문번호", en: "Order No." },
    createdAt: { kr: "주문일", en: "Order Date" },
    customerName: { kr: "계약자명", en: "Contract Holder" },
    phoneNumber: { kr: "연락처", en: "Contact Number" },
    fullAddress: { kr: "주소", en: "Shipping Address" },
    email: { kr: "이메일", en: "Email Address" },
    paymentMethod: {
      kr: "클럽 회원비 결제등록 정보",
      en: "WFC Membership Fee Payment Method",
    },
    accountHolderName: { kr: "예금주명", en: "Account Holder Name" },
    birthDate: { kr: "생년월일", en: "Date of Birth" },
    bankName: { kr: "은행명", en: "Bank Name" },
    displayMaskBank: { kr: "계좌번호", en: "Bank Account Number" },
    creditCardBankName: { kr: "카드사", en: "Credit Card Company" },
    displayMaskCard: { kr: "카드번호", en: "Credit Card Number" },
    cardHolderName: { kr: "카드소유자명", en: "Card Holder Name" },
    cardBirthDate: { kr: "생년월일", en: "Date of Birth" },
    creditCardExpirationDate: {
      kr: "카드유효기간",
      en: "Credit Card Expiration Date",
    },
    childName1: { kr: "자녀이름1", en: "Child's Name1" },
    childGender1: { kr: "성별1", en: "Child's Gender1" },
    childBirthDate1: { kr: "생년월일1", en: "Child's Date of Birth1" },
    childName2: { kr: "자녀이름2", en: "Child's Name2" },
    childGender2: { kr: "성별2", en: "Child's Gender2" },
    childBirthDate2: { kr: "생년월일2", en: "Child's Date of Birth2" },
    childName3: { kr: "자녀이름3", en: "Child's Name3" },
    childGender3: { kr: "성별3", en: "Child's Gender3" },
    childBirthDate3: { kr: "생년월일3", en: "Child's Date of Birth3" },
  };
  const columnTitle = (key: string) =>
    isHK || isTW ? columnTitles[key].en : columnTitles[key].kr;

  const columns: ColumnsType<EntryPackageQueryDataType> = [
    {
      title: columnTitle("orderId"),
      dataIndex: "orderId",
      key: "orderId",
      render: (v, i) => (v ? `${i.packageNo}-${v}` : "N/A"),
      ...getColumnSearchProps("orderId"),
    },
    {
      title: columnTitle("createdAt"),
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a, b) => {
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return a.createdAt.localeCompare(b.createdAt);
      },
      render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "N/A"),
      ...getColumnDateRangeProps(),
    },
    {
      title: columnTitle("customerName"),
      dataIndex: "customerName",
      key: "customerName",
      render: (v, _) => (v ? v : "N/A"),
      ...getColumnSearchProps("customerName"),
    },
    {
      title: columnTitle("phoneNumber"),
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      render: (v, _) => (v ? v : "N/A"),
      ...getColumnSearchProps("phoneNumber"),
    },
    {
      title: columnTitle("fullAddress"),
      dataIndex: "fullAddress",
      key: "fullAddress",
      render: (v, _) => (v ? v : "N/A"),
    },
    ...(isHK
      ? []
      : [
          {
            title: columnTitle("email"),
            dataIndex: "email",
            key: "email",
            render: (v: string, _: EntryPackageQueryDataType) =>
              v ? v : "N/A",
            ...getColumnSearchProps("email"),
          },
        ]),
    ...(isTW
      ? []
      : [
          {
            title: columnTitle("paymentMethod"),
            dataIndex: "paymentMethod",
            key: "paymentMethod",
            render: (v: string, _: EntryPackageQueryDataType) =>
              v ? v.replace("_", " ") : "N/A",
          },
          {
            title: columnTitle("accountHolderName"),
            dataIndex: ["memberPaymentInfo", "accountHolderName"],
            key: "accountHolderName",
            render: (v: string, i: EntryPackageQueryDataType) =>
              v && i.memberPaymentInfo?.paymentType === 1 ? v : "N/A",
          },
          {
            title: columnTitle("birthDate"),
            dataIndex: ["memberPaymentInfo", "birthDate"],
            key: "birthDate",
            render: (v: string, i: EntryPackageQueryDataType) =>
              v && i.memberPaymentInfo?.paymentType === 1 ? v : "N/A",
          },
          {
            title: columnTitle("bankName"),
            dataIndex: ["memberPaymentInfo", "bankName"],
            key: "bankName",
            render: (v: string, i: EntryPackageQueryDataType) =>
              v && i.memberPaymentInfo?.paymentType === 1 ? v : "N/A",
          },
          {
            title: columnTitle("displayMaskBank"),
            dataIndex: ["memberPaymentInfo", "displayMask"],
            key: "displayMaskBank",
            render: (v: string, i: EntryPackageQueryDataType) =>
              v && i.memberPaymentInfo?.paymentType === 1 ? v : "N/A",
          },
          {
            title: columnTitle("creditCardBankName"),
            dataIndex: isHK
              ? ["memberPaymentInfo", "cardIssuer"]
              : ["memberPaymentInfo", "creditCardBankName"],
            key: "creditCardBankName",
            render: (v: string, i: EntryPackageQueryDataType) =>
              v && i.memberPaymentInfo?.paymentType === 2 ? v : "N/A",
          },
          {
            title: columnTitle("displayMaskCard"),
            dataIndex: ["memberPaymentInfo", "displayMask"],
            key: "displayMaskCard",
            render: (v: string, i: EntryPackageQueryDataType) =>
              v && i.memberPaymentInfo?.paymentType === 2 ? v : "N/A",
          },
          {
            title: columnTitle("cardHolderName"),
            dataIndex: ["memberPaymentInfo", "accountHolderName"],
            key: "cardHolderName",
            render: (v: string, i: EntryPackageQueryDataType) =>
              v && i.memberPaymentInfo?.paymentType === 2 ? v : "N/A",
          },
          {
            title: columnTitle("cardBirthDate"),
            dataIndex: ["memberPaymentInfo", "birthDate"],
            key: "cardBirthDate",
            render: (v: string, i: EntryPackageQueryDataType) =>
              v && i.memberPaymentInfo?.paymentType === 2 ? v : "N/A",
          },
          {
            title: columnTitle("creditCardExpirationDate"),
            dataIndex: ["memberPaymentInfo", "expiryMonth"],
            key: "creditCardExpirationDate",
            render: (_: unknown, i: EntryPackageQueryDataType) =>
              i.memberPaymentInfo?.paymentType === 2
                ? `${i.memberPaymentInfo.expiryMonth}/${i.memberPaymentInfo.expiryYear}`
                : "N/A",
          },
        ]),
    {
      title: columnTitle("childName1"),
      render: (_, record) => record.memberChildren?.[0]?.childName || "",
    },
    {
      title: columnTitle("childGender1"),
      render: (_, record) => record.memberChildren?.[0]?.childGender || "",
    },
    {
      title: columnTitle("childBirthDate1"),
      render: (_, record) =>
        record.memberChildren?.[0]?.childBirthDate?.split("T")[0] || "",
    },
    {
      title: columnTitle("childName2"),
      render: (_, record) => record.memberChildren?.[1]?.childName || "",
    },
    {
      title: columnTitle("childGender2"),
      render: (_, record) => record.memberChildren?.[1]?.childGender || "",
    },
    {
      title: columnTitle("childBirthDate2"),
      render: (_, record) =>
        record.memberChildren?.[1]?.childBirthDate?.split("T")[0] || "",
    },
    {
      title: columnTitle("childName3"),
      render: (_, record) => record.memberChildren?.[2]?.childName || "",
    },
    {
      title: columnTitle("childGender3"),
      render: (_, record) => record.memberChildren?.[2]?.childGender || "",
    },
    {
      title: columnTitle("childBirthDate3"),
      render: (_, record) =>
        record.memberChildren?.[2]?.childBirthDate?.split("T")[0] || "",
    },
  ];

  if (userProgramsLoading) {
    return <Spinner />;
  }

  if (
    userPrograms &&
    !userPrograms.wf_adminFirst_afUserProgram?.edges?.find(
      (item) => item.node.afProgram?.programName === "Entry Package Query",
    )
  ) {
    return <NoPermission />;
  }

  return (
    <div id="entryPackageQueryWrapper">
      <ContentTitle title="Entry Package Query" />
      <div className="table-container">
        {currentUser?.afUserMarkets?.length &&
          currentUser?.afUserMarkets?.length > 1 && (
            <div className="flex justify-end p-5">
              <div>
                <MarketDropdown
                  selectedMarket={selectedMarket}
                  setSelectedMarket={handleMarketChange}
                  alwaysShow={true}
                  includeJp={false}
                />
              </div>
            </div>
          )}
        <Form
          form={form}
          name="entry-package-query-form"
          className="flex flex-wrap gap-4"
        >
          <Form.Item label="Order Date" name="orderDate" className="w-4/12">
            <RangePicker />
          </Form.Item>
          <Form.Item label="Order ID" name="orderId" className="w-4/12">
            <Input />
          </Form.Item>
          <Form.Item
            label="Customer Name"
            name="customerName"
            className="w-4/12"
          >
            <Input />
          </Form.Item>
          <Form.Item label="Phone Number" name="phoneNumber" className="w-4/12">
            <Input />
          </Form.Item>

          <div className="flex w-full">
            <Form.Item shouldUpdate noStyle>
              {() => (
                <Button
                  type="primary"
                  onClick={() => {
                    form
                      .validateFields()
                      .then(() => {
                        handleSubmit();
                      })
                      .catch((e) => console.log(e));
                    setShowDownload(true);
                  }}
                >
                  Query
                </Button>
              )}
            </Form.Item>
            <Button
              className="ms-2"
              onClick={() => {
                form.resetFields();
                setOrderStartDate("");
                setOrderEndDate("");
                setOrderId("");
                setCustomerName("");
                setPhoneNumber("");
                setShowData(false);
                setShowDownload(false);
              }}
            >
              Clear
            </Button>
            {dataDisplay.length > 0 && showDownload && (
              <Button className="ms-2" onClick={handleDownload}>
                Download
              </Button>
            )}
          </div>
        </Form>
      </div>
      {showData && (
        <div className="table-container" style={{ marginTop: -20 }}>
          <Table
            columns={columns}
            dataSource={dataDisplay}
            scroll={{ x: "max-content" }}
            onChange={(_, __, ___, extra) => {
              setFilteredData(extra.currentDataSource);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default EntryPackageQuery;
